import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, token as session } from "../api/client";
import { getConfig } from "../config";
import { useEvent } from "../context/EventProvider";
import {
  asSmsMessage,
  asSmsState,
  withSms,
  withSmsState,
  type SmsInbox,
  type SmsMessage,
  type SmsState,
} from "./sms";
import type { LivePayload } from "./types";

/**
 * ═══ LE DIRECT, LU PUIS POUSSÉ ═══
 *
 * Une seule adresse pour les trois écrans : `GET /live` répond ce que le RÔLE du
 * demandeur a le droit de voir, et le websocket pousse la même chose quand ça
 * change. Le serveur construit les deux avec la même fonction, exprès.
 *
 * ⚠️ LA LECTURE D'ABORD, LA POUSSÉE ENSUITE, et jamais l'inverse. Un écran qui
 * n'afficherait que ce qu'on lui pousse resterait vide jusqu'au premier
 * changement : quelqu'un qui arrive au milieu d'un sondage déjà ouvert ne verrait
 * rien avant le vote suivant. La lecture donne l'état, la poussée donne la suite.
 *
 * ⚠️ ET LA POUSSÉE ÉCRIT DANS LE CACHE, elle ne déclenche pas une relecture. Huit
 * cents téléphones qui rappellent l'API à chaque vote, c'est huit cents requêtes
 * par vote. Le message CONTIENT déjà l'état : il remplace la donnée en place.
 *
 * ⚠️ SAUF `myAnswers`, QUE LA POUSSÉE NE PORTE PAS. Cette partie diffère d'une
 * personne à l'autre, donc le serveur ne la diffuse pas (voir `livePayload.js`).
 * Écraser bêtement le cache l'effacerait, et les boutons de vote reviendraient
 * après le vote de quelqu'un d'autre. On la reporte.
 */

/**
 * TOUT LE DIRECT D'UN ÉVÉNEMENT, quel que soit le demandeur. Sert à invalider
 * sans avoir à savoir qui est en train de regarder.
 */
export const liveScope = (domainId: number, eventId: number) =>
  ["live", domainId, eventId] as const;

/**
 * LA CHARGE D'UN DEMANDEUR PRÉCIS.
 *
 * ⚠️ LE JETON D'ÉCRAN FAIT PARTIE DE LA CLÉ. Le serveur répond selon le RÔLE :
 * trois charges différentes derrière la même adresse. Sans le jeton dans la clé,
 * les trois partagent une entrée, et `staleTime` la sert telle quelle pendant une
 * minute. Passer du mur à la régie servait alors la charge « écran » et enfermait
 * l'organisateur hors de sa console ; l'inverse servait la charge « contrôle » au
 * vidéoprojecteur, questions non modérées comprises. Coller un nouveau jeton après
 * une révocation ne relisait rien non plus, la clé n'ayant pas bougé.
 */
export const liveKey = (
  domainId: number,
  eventId: number,
  screenToken?: string | null,
) => [...liveScope(domainId, eventId), screenToken ?? null] as const;

/**
 * LES SMS REÇUS PAR LA RÉGIE (voir `sms.ts`).
 *
 * ⚠️ HORS DE `liveScope`, EXPRÈS. Chaque geste de la régie invalide tout le direct :
 * rangée dessous, la liste — jusqu'à cinq cents SMS — se serait relue à chaque
 * sondage lancé. Elle se tient à jour par le canal, et se relit à la reconnexion.
 */
export const smsKey = (domainId: number, eventId: number) =>
  ["live-sms", domainId, eventId] as const;

/**
 * UNE PAGE DE LA LISTE, au-delà de la première (voir `turnPage`).
 *
 * ⚠️ RANGÉE SOUS `smsKey` : la reconnexion la relit avec la liste. Le canal, lui, n'y
 * pose rien — `setQueryData` ne touche que la clé exacte, la première page.
 */
export const smsPageKey = (domainId: number, eventId: number, page: number, upTo: number) =>
  [...smsKey(domainId, eventId), "page", page, upTo] as const;

/**
 * LES DEUX LISTES QUE SEULE LA RÉGIE LIT : ses activités, et ses écrans de salle
 * (voir `control.ts`). Rangées ici parce que le canal les relit.
 *
 * ⚠️ HORS DE `liveScope` elles aussi, et pour la même raison que les SMS : la liste des
 * activités porte les résultats de chacune, et `liveScope` s'invalide à chaque geste.
 */
export const activitiesKey = (domainId: number, eventId: number) =>
  ["live-activities", domainId, eventId] as const;

/** Le code de l'écran de salle, que la régie montre (voir `control.ts`). */
export const screenCodeKey = (domainId: number, eventId: number) =>
  ["live-screen-code", domainId, eventId] as const;

/**
 * CE QUI ARRIVE PAR LE CANAL, POSÉ SUR CE QU'ON AVAIT.
 *
 * ⚠️ `myAnswers` NE SE REPORTE QUE SUR LA MÊME ACTIVITÉ. Cette partie de la charge
 * diffère d'une personne à l'autre, donc le serveur ne la diffuse pas (voir
 * `livePayload.js`) : sans report, les boutons de vote reviendraient dès que
 * QUELQU'UN D'AUTRE répond. Mais elle est comptée PAR activité, et la reporter sans
 * regarder laquelle collait les réponses du sondage précédent sur le suivant :
 * quelqu'un qui avait voté une fois lisait « votre réponse est enregistrée » sur une
 * question jamais posée, sans boutons, pour toute la suite de la session. Le tableau
 * n'étant plus jamais vide, la garde ne repassait plus ; seule la relecture de trente
 * secondes réparait.
 *
 * ⚠️ FONCTION À PART POUR ÊTRE VÉRIFIABLE. Elle vivait dans la fermeture d'un
 * `useCallback` : rien ne pouvait l'atteindre sans monter un composant, et le projet
 * ne rend pas de composants dans ses tests.
 */
export function mergeLive(
  current: LivePayload | undefined,
  next: LivePayload,
): LivePayload {
  if (!current?.myAnswers) return next;
  if (current.activity?.id !== next.activity?.id) return next;
  return { ...next, myAnswers: current.myAnswers };
}

/** Une réaction qui vient d'arriver, le temps de la faire monter à l'écran. */
export type Reaction = { id: string; icon: string };

/** Au-delà, les plus anciennes tombent : un mur montre ce qui arrive. */
const MAX_REACTIONS = 60;

type Options = {
  /**
   * Le jeton d'écran de salle, quand c'est une salle. Un participant et la régie
   * n'en ont pas : ils portent leur session.
   */
  screenToken?: string | null;
  /** Coupé quand l'écran n'est pas monté, pour ne pas tenir une socket pour rien. */
  enabled?: boolean;
};

async function fetchLive(
  domainId: number,
  eventId: number,
  screenToken: string | null | undefined,
  signal: AbortSignal,
): Promise<LivePayload> {
  const query = screenToken ? `?token=${encodeURIComponent(screenToken)}` : "";
  return apiFetch<LivePayload>(
    `/domains/${domainId}/events/${eventId}/live${query}`,
    { api: "apix", signal },
  );
}

export function useLive({ screenToken, enabled = true }: Options = {}) {
  const event = useEvent();
  const client = useQueryClient();

  const domainId = event?.domainId ?? 0;
  const eventId = event?.eventId ?? 0;
  const on = enabled && !!event;

  const query = useQuery({
    queryKey: liveKey(domainId, eventId, screenToken),
    queryFn: ({ signal }) => fetchLive(domainId, eventId, screenToken, signal),
    enabled: on,
    /**
     * ⚠️ UN FILET, PAS LE MÉCANISME. Le websocket fait le travail ; ce rappel
     * existe pour le cas où il n'y a pas d'adresse de canal configurée, ou pour le
     * trou entre une coupure réseau et la reconnexion. Trente secondes ne font pas
     * un direct, et c'est voulu : si ça devient la seule source, ça se voit.
     */
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const apply = useCallback(
    (next: LivePayload) => {
      client.setQueryData<LivePayload>(
        liveKey(domainId, eventId, screenToken),
        (current) => mergeLive(current, next),
      );
    },
    [client, domainId, eventId, screenToken],
  );

  /**
   * Ce que le CANAL a dit de la séquence de réactions. `undefined` veut dire qu'il
   * n'a rien dit (pas de canal, ou pas encore connecté) ; `null` veut dire qu'il a
   * dit « aucune séquence ouverte », ce qui n'est pas la même chose et doit primer
   * sur la charge lue avant.
   */
  const [pushedSequence, setPushedSequence] = useState<number | null | undefined>(
    undefined,
  );

  /**
   * LES RÉACTIONS QUI VIENNENT D'ARRIVER, pour le mur de la salle.
   *
   * ⚠️ BORNÉES, ET C'EST LE POINT. Une salle de huit cents personnes envoie des
   * milliers de réactions en une session ; les garder toutes ferait grossir la
   * mémoire d'un poste qui reste allumé des heures. Seules les dernières comptent :
   * un mur montre ce qui arrive, pas un historique.
   */
  const [reactions, setReactions] = useState<Reaction[]>([]);

  useLiveSocket({
    eventId,
    screenToken,
    enabled: on,
    onMessage: apply,
    onSequence: setPushedSequence,
    onReaction: (icon) =>
      setReactions((current) =>
        [...current, { id: `${icon}-${current.length}-${performance.now()}`, icon }].slice(-MAX_REACTIONS),
      ),
    /* Seule la régie les reçoit : le serveur ne les pousse qu'aux connexions `control`. */
    onSms: (message) =>
      client.setQueryData<SmsInbox>(smsKey(domainId, eventId), (current) =>
        withSms(current, message),
      ),
    onSmsState: (state) =>
      client.setQueryData<SmsInbox>(smsKey(domainId, eventId), (current) =>
        withSmsState(current, state),
      ),
    /**
     * ⚠️ UNE AUTRE RÉGIE A CHANGÉ UNE LISTE (#343). À deux sur la régie, celui qui
     * terminait une activité la voyait changer de groupe, l'autre non : la charge du
     * direct lui arrivait, la liste ne se relisait qu'à SES gestes, et il fallait
     * recharger la page. Le message ne porte pas la liste, il dit de la relire.
     */
    onListChanged: (list) =>
      void client.invalidateQueries({
        queryKey: (list === "screenCode" ? screenCodeKey : activitiesKey)(domainId, eventId),
      }),
    /* Ce qui a été poussé pendant la coupure est perdu : on relit l'état complet. */
    onReconnect: () => {
      void client.invalidateQueries({ queryKey: liveScope(domainId, eventId) });
      void client.invalidateQueries({ queryKey: smsKey(domainId, eventId) });
      void client.invalidateQueries({ queryKey: activitiesKey(domainId, eventId) });
      void client.invalidateQueries({ queryKey: screenCodeKey(domainId, eventId) });
    },
  });

  /**
   * `sequenceId` : la séquence de réactions ouverte, s'il y en a une.
   *
   * ⚠️ LE CANAL PRIME, LA LECTURE RATTRAPE. Le serveur l'annonce à la connexion
   * (`NEW_SEQUENCE`) et à chaque changement, et c'est le mécanisme de la V1 : on le
   * garde, il évite une seconde vérité sur « les réactions sont-elles ouvertes ».
   * Mais la tenir du SEUL canal la rendait absente partout où il n'y a pas
   * d'adresse de websocket déclarée — la production aujourd'hui — et le bloc de
   * réactions ne s'affichait alors jamais, pas même en dégradé. La charge la porte
   * donc aussi. Une valeur poussée, `null` compris, l'emporte : elle est plus
   * fraîche.
   */
  const sequenceId =
    pushedSequence !== undefined ? pushedSequence : (query.data?.sequenceId ?? null);

  return { ...query, sequenceId, reactions };
}

/**
 * LA SOCKET, ET SA RECONNEXION.
 *
 * ⚠️ LE DÉLAI DOUBLE À CHAQUE ÉCHEC. La V1 se reconnecte toutes les secondes,
 * dix fois : quand c'est le serveur qui tombe, huit cents téléphones lui tapent
 * dessus huit cents fois par seconde au moment précis où il se relève. Ici,
 * une seconde, puis deux, puis quatre, plafonné à trente.
 */
function useLiveSocket({
  eventId,
  screenToken,
  enabled,
  onMessage,
  onSequence,
  onReaction,
  onSms,
  onSmsState,
  onListChanged,
  onReconnect,
}: {
  eventId: number;
  screenToken?: string | null;
  enabled: boolean;
  onMessage: (payload: LivePayload) => void;
  onSequence?: (sequenceId: number | null) => void;
  onReaction?: (icon: string) => void;
  onSms?: (message: SmsMessage) => void;
  onSmsState?: (state: SmsState) => void;
  /** Une liste de la régie a changé ailleurs : à relire. */
  onListChanged?: (list: "activities" | "screenCode") => void;
  /** Appelé après une reconnexion (pas à la première ouverture). */
  onReconnect?: () => void;
}) {
  /* La dernière fonction reçue, sans relancer la connexion à chaque rendu du parent. */
  const handler = useRef(onMessage);
  handler.current = onMessage;
  const sequence = useRef(onSequence);
  sequence.current = onSequence;
  const reaction = useRef(onReaction);
  reaction.current = onReaction;
  const sms = useRef(onSms);
  sms.current = onSms;
  const smsState = useRef(onSmsState);
  smsState.current = onSmsState;
  const listChanged = useRef(onListChanged);
  listChanged.current = onListChanged;
  const reconnect = useRef(onReconnect);
  reconnect.current = onReconnect;

  useEffect(() => {
    if (!enabled || !eventId) return;

    const { wsUrl } = getConfig();
    // Pas d'adresse déclarée : l'écran vit sur le rappel de la requête. Voir `env.ts`.
    if (!wsUrl) return;

    let socket: WebSocket | null = null;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let closed = false;

    const open = () => {
      if (closed) return;

      // Lu à CHAQUE ouverture : après une reconnexion (code, lien), le jeton a changé.
      const credential = screenToken || session.read();
      const query = new URLSearchParams({ eventId: String(eventId) });
      if (credential) query.set("token", credential);

      socket = new WebSocket(`${wsUrl}?${query.toString()}`);

      socket.onopen = () => {
        /* ⚠️ CE QUI A ÉTÉ POUSSÉ PENDANT LA COUPURE EST PERDU : on relit, sinon le
           téléphone reste sur l'activité d'avant jusqu'au filet de trente secondes. */
        if (attempt > 0) reconnect.current?.();
        attempt = 0;
      };

      socket.onmessage = (message) => {
        try {
          const parsed = JSON.parse(String(message.data)) as {
            type?: string;
            data?: LivePayload & { sequenceId?: number | null };
          };
          // Le canal porte l'état du direct, la séquence de réactions ouverte, les
          // réactions, et pour la régie les SMS et ses listes à relire. Tout autre
          // message ne nous regarde pas.
          if (parsed?.type === "LIVE" && parsed.data) {
            handler.current(parsed.data as LivePayload);
          }
          if (parsed?.type === "NEW_SEQUENCE") {
            sequence.current?.(parsed.data?.sequenceId ?? null);
          }
          if (parsed?.type === "NEW_EMOTION") {
            const icon = (parsed.data as { iconUrl?: string } | undefined)?.iconUrl;
            if (icon) reaction.current?.(icon);
          }
          if (parsed?.type === "SMS") {
            const message = asSmsMessage(parsed.data);
            if (message) sms.current?.(message);
          }
          if (parsed?.type === "SMS_STATE") {
            const state = asSmsState(parsed.data);
            if (state) smsState.current?.(state);
          }
          if (parsed?.type === "ACTIVITIES") listChanged.current?.("activities");
          if (parsed?.type === "SCREEN_CODE") listChanged.current?.("screenCode");
          /* Le code de CET écran de salle a été révoqué ou remplacé : on relit tout, le
             serveur refuse, et l'écran dit comment se reconnecter au lieu de rester
             figé sur sa dernière image. */
          if (parsed?.type === "SCREEN_CODE_CHANGED") reconnect.current?.();
        } catch {
          /* Un message illisible ne doit pas emporter l'écran. */
        }
      };

      socket.onclose = () => {
        if (closed) return;
        /**
         * ⚠️ AVEC UNE PART DE HASARD, sinon le doublement ne sert à rien. Quand
         * c'est le serveur qui tombe, toute la salle se déconnecte à la même
         * seconde et repart donc sur le même palier : huit cents reconnexions à
         * 1000 ms pile, puis à 2000 ms pile. La gigue étale la vague.
         */
        const base = Math.min(30_000, 1000 * 2 ** attempt);
        const wait = base * (0.7 + Math.random() * 0.6);
        attempt += 1;
        timer = setTimeout(open, wait);
      };
    };

    open();

    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      /* `onclose` retiré AVANT de fermer : sinon la fermeture volontaire est lue
         comme une coupure et relance une reconnexion sur un écran démonté. */
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
    };
  }, [enabled, eventId, screenToken]);
}
